import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ProviderLogo from '~/components/Shared/ProviderLogo/ProviderLogo';
import { Button } from '~/eds/src/components/Button/Button';
import { Card } from '~/eds/src/components/Card/Card';
import { Grid } from '~/eds/src/components/Grid/Grid';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Text } from '~/eds/src/components/Text/Text';
import { HtmlEntityType } from '~/eds/src/enums/HtmlEntityType';
import { AribaIntegrationType } from '~/enums/AribaIntegrationType';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { useIntl } from 'react-intl';
import { slices } from '~/redux/slices';

import AribaFooter from '../AribaFooter';

function FileConfigSetup() {
  const intl = useIntl();
  const dispatch = useDispatch();

  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  );

  const { provider } = activeSyncPair;

  const oneTimeImportDescription = intl.formatMessage({
    id: 'FileConfigSetup.oneTimeImportDescription',
    defaultMessage:
      '<strong>Select files for a one-time import</strong><br/>- Upload CSV with Ariba documents<br/>- Select fields to transfer between the two systems<br/>- Evisort does not monitor for changes in Ariba<br/><br/><strong>Best for:</strong> historical contracts where the files or data are not changing.',
    description: 'Description for one-time import option',
  });

  const continuousSyncDescription = intl.formatMessage({
    id: 'FileConfigSetup.continuousSyncDescription',
    defaultMessage:
      "<strong>Select files for continuous data sync</strong><br/>- Use filters to discover new Contract Workspaces as they're created<br/>- Select fields to transfer between the two systems<br/>- Evisort will monitor for changes in Ariba<br/><br/><strong>Best for:</strong> situations where you want to monitor and process new Contract Workspaces.",
    description: 'Description for continuous sync option',
  });

  const handleBack = () => {
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.MultiAPIs,
      ),
    );
  };

  return (
    <Layout align="center" direction="column" spacing={8}>
      <Layout align="center" py={2} spacing={6}>
        <ProviderLogo provider="evisort" />
        <Text color="text.quiet">{HtmlEntityType.DoubleDash}</Text>
        <ProviderLogo provider={provider} />
      </Layout>
      <Grid columnSpacing={8} columns={2}>
        <Card status="info" mode="bordered" px={8} py={4}>
          <Layout direction="column" py={2} spacing={6}>
            <Markdown text={oneTimeImportDescription} />
            <Layout>
              <Button
                text={intl.formatMessage({
                  id: 'FileConfigSetup.uploadButton',
                  defaultMessage: 'Upload',
                  description: 'Button text for upload option',
                })}
                variant="secondary"
                onClick={() => {
                  dispatch(
                    slices.integrations.actions.setActiveSyncPair({
                      ...activeSyncPair,
                      syncType: AribaIntegrationType.ONE_TIME,
                    }),
                  );
                  dispatch(
                    slices.integrations.actions.setManageSyncPairStage(
                      ManageSyncPairStageType.FolderAriba,
                    ),
                  );
                }}
              />
            </Layout>
          </Layout>
        </Card>
        <Card status="info" mode="bordered" px={8} py={4}>
          <Layout direction="column" py={2} spacing={6}>
            <Markdown text={continuousSyncDescription} />
            <Layout>
              <Button
                text={intl.formatMessage({
                  id: 'FileConfigSetup.syncButton',
                  defaultMessage: 'Sync',
                  description: 'Button text for sync option',
                })}
                variant="secondary"
                onClick={() => {
                  dispatch(
                    slices.integrations.actions.setActiveSyncPair({
                      ...activeSyncPair,
                      syncType: AribaIntegrationType.CONTINUOUS,
                    }),
                  );
                  dispatch(
                    slices.integrations.actions.setManageSyncPairStage(
                      ManageSyncPairStageType.FolderAriba,
                    ),
                  );
                }}
              />
            </Layout>
          </Layout>
        </Card>
      </Grid>
      <AribaFooter
        secondaryAction={{
          text: intl.formatMessage({
            id: 'FileConfigSetup.backButton',
            defaultMessage: 'Back',
            description: 'Button text for going back',
          }),
          onClick: handleBack,
        }}
      />
    </Layout>
  );
}

export default FileConfigSetup;
